import actions from "~/shared/actions";
import type { Action } from "~/utils/actions";

interface IsFetching {
	isFetching: boolean;
}

interface Customer extends IsFetching {
	hasFetchedCustomer: boolean;
	hasFetchedF2100AccountNumber: boolean;
	hasUpdatedFatcaData: boolean;
	isFetchingF2100AccountNumber: boolean;
	isUpdatingEmail: boolean;
	isUpdatingPhoneNumber: boolean;
	isUpdatingFatcaData: boolean;
	hasFetchedLoForvaltningsform: boolean;
}

interface Policies extends IsFetching {
	hasFetchedList: boolean;
	isFetchingSalaryHistory: boolean;
	isCreatingPaymentDeal: boolean;
	isDeletingPaymentDeal: boolean;
	isFetchingDetails: boolean;
	isFetchingNjordDetails: boolean;
	isFetchingHoldings: boolean;
	isUpdatingUtbetalingStart: boolean;
	isUpdatingForvaltning: boolean;
	isUpdatingKapitalforvalter: boolean;
	isUpdatingNedtrapping: boolean;
	isUpdatingPaymentDeal: boolean;
	isUpdatingSavingAmount: boolean;
	isStoppingSavings: boolean;
	isPerformingLoProduktbytte: boolean;
	hasPerformedLoProduktbytte: boolean;
}

interface BankAccounts {
	hasFetchedList: boolean;
	isFetchingBankAccounts: boolean;
}

interface Countries {
	isFetchingCountries: boolean;
	hasFetchedCountries: boolean;
}

interface HoldingsHistory {
	isFetchingHoldingsHistory: boolean;
}

interface InvestmentPrograms {
	isFetchingInvestmentPrograms: boolean;
	isFetchingOpenInvestmentFunds: boolean;
	isUpdatingInvestmentProfile: boolean;
	isUpdatingOpenInvestmentFunds: boolean;
	hasFetchedOpenInvestmentFunds: boolean;
}

interface NewPolicy {
	isConfirmingPolicy: boolean;
}

interface Prognosis {
	isFetchingPrognosis: boolean;
}

interface Samtykke {
	isFetchingTexts: boolean;
	hasFetchedTexts: boolean;
}

interface SL {
	isCancellingSl: boolean;
	isCreatingSl: boolean;
	isFetchingSlAvtaler: boolean;
	hasCancelledSl: boolean;
	hasCreatedSl: boolean;
	hasFetchedSlAvtaler: boolean;
}

interface NorskPensjon {
	isFetchingPkbs: boolean;
}

interface FlyttPkb {
	isCreatingWorkOrders: boolean;
}

interface AccountTransactions extends IsFetching {
	[key: number]: {
		[key: string | number]: {
			isFetching: boolean;
			hasFetched?: boolean;
		};
	};
}

interface Dokumenter extends IsFetching {
	hasFetched: boolean;
	isFetchingGamle: boolean;
	hasFetchedGamle: boolean;
}

export interface MetadataState {
	accountTransactions: AccountTransactions;
	bankAccounts: BankAccounts;
	countries: Countries;
	customer: Customer;
	documents: Dokumenter;
	holdingsHistory: HoldingsHistory;
	investmentPrograms: InvestmentPrograms;
	policies: Policies;
	newPolicy: NewPolicy;
	prognosis: Prognosis;
	samtykke: Samtykke;
	sl: SL;
	norskPensjon: NorskPensjon;
	flyttPkb: FlyttPkb;
}

/*
 * This turned out to be a bad pattern that's expensive to get rid of. Don't do this.
 */
export const defaultState: MetadataState = {
	accountTransactions: {
		isFetching: false,
	},
	bankAccounts: {
		hasFetchedList: false,
		isFetchingBankAccounts: false,
	},
	countries: {
		isFetchingCountries: false,
		hasFetchedCountries: false,
	},
	customer: {
		hasFetchedCustomer: false,
		hasFetchedF2100AccountNumber: false,
		hasUpdatedFatcaData: false,
		isFetching: false,
		isFetchingF2100AccountNumber: false,
		isUpdatingEmail: false,
		isUpdatingPhoneNumber: false,
		isUpdatingFatcaData: false,
		hasFetchedLoForvaltningsform: false,
	},
	documents: {
		isFetching: false,
		hasFetched: false,
		isFetchingGamle: false,
		hasFetchedGamle: false,
	},
	holdingsHistory: {
		isFetchingHoldingsHistory: false,
	},
	investmentPrograms: {
		isFetchingInvestmentPrograms: false,
		isFetchingOpenInvestmentFunds: false,
		isUpdatingInvestmentProfile: false,
		isUpdatingOpenInvestmentFunds: false,
		hasFetchedOpenInvestmentFunds: false,
	},
	policies: {
		hasFetchedList: false,
		isFetchingSalaryHistory: false,
		isCreatingPaymentDeal: false,
		isDeletingPaymentDeal: false,
		isFetching: false,
		isFetchingDetails: false,
		isFetchingNjordDetails: false,
		isFetchingHoldings: false,
		isUpdatingUtbetalingStart: false,
		isUpdatingForvaltning: false,
		isUpdatingKapitalforvalter: false,
		isUpdatingNedtrapping: false,
		isUpdatingPaymentDeal: false,
		isUpdatingSavingAmount: false,
		isStoppingSavings: false,
		isPerformingLoProduktbytte: false,
		hasPerformedLoProduktbytte: false,
	},
	newPolicy: {
		isConfirmingPolicy: false,
	},
	prognosis: {
		isFetchingPrognosis: false,
	},
	samtykke: {
		isFetchingTexts: false,
		hasFetchedTexts: false,
	},
	sl: {
		isCancellingSl: false,
		isCreatingSl: false,
		isFetchingSlAvtaler: false,
		hasCancelledSl: false,
		hasCreatedSl: false,
		hasFetchedSlAvtaler: false,
	},
	norskPensjon: {
		isFetchingPkbs: false,
	},
	flyttPkb: {
		isCreatingWorkOrders: false,
	},
};

const accountTransactionsReducer = (state: AccountTransactions, action: Action): AccountTransactions => {
	switch (action.type) {
		case actions.ACCOUNT_TRANSACTIONS_REQUEST:
			return { ...state, isFetching: true };
		case actions.ACCOUNT_TRANSACTION_REQUEST:
			return {
				...state,
				[action.data.policyNumber]: {
					...state[action.data.policyNumber],
					[action.data.transactionId]: {
						isFetching: true,
					},
				},
			};
		case actions.ACCOUNT_TRANSACTIONS_RESPONSE:
			return defaultState.accountTransactions;
		case actions.ACCOUNT_TRANSACTION_RESPONSE: {
			return {
				...state,
				[action.data.policyNumber]: {
					...state[action.data.policyNumber],
					[action.data.transaction.id]: {
						isFetching: false,
						hasFetched: true,
					},
				},
			};
		}
		default:
			return state;
	}
};

const bankAccountsReducer = (state: BankAccounts, action: Action): BankAccounts => {
	switch (action.type) {
		case actions.BANK_ACCOUNTS_REQUEST:
			return {
				...state,
				hasFetchedList: false,
				isFetchingBankAccounts: true,
			};
		case actions.BANK_ACCOUNTS_RESPONSE:
			return {
				...state,
				hasFetchedList: true,
				isFetchingBankAccounts: false,
			};
		default:
			return state;
	}
};

const countriesReducer = (state: Countries, action: Action): Countries => {
	switch (action.type) {
		case actions.COUNTRIES_REQUEST:
			return { ...state, isFetchingCountries: true };
		case actions.COUNTRIES_RESPONSE:
			return {
				...state,
				isFetchingCountries: false,
				hasFetchedCountries: true,
			};
		default:
			return state;
	}
};

const customerReducer = (state: Customer, action: Action): Customer => {
	switch (action.type) {
		case actions.CUSTOMER_RESPONSE:
			return { ...state, isFetching: false, hasFetchedCustomer: true };
		case actions.CUSTOMER_REQUEST:
			return { ...state, isFetching: true, hasFetchedCustomer: false };
		case actions.EMAIL_UPDATE_RESPONSE:
			return { ...state, isUpdatingEmail: false };
		case actions.EMAIL_UPDATE_REQUEST:
			return { ...state, isUpdatingEmail: true };
		case actions.FATCA_DATA_RESPONSE:
			return { ...state, hasUpdatedFatcaData: true };
		case actions.PHONE_NUMBER_UPDATE_REQUEST:
			return { ...state, isUpdatingPhoneNumber: true };
		case actions.PHONE_NUMBER_UPDATE_RESPONSE:
			return { ...state, isUpdatingPhoneNumber: false };
		case actions.FATCA_DATA_UPDATE_REQUEST:
			return { ...state, isUpdatingFatcaData: true };
		case actions.FATCA_DATA_UPDATE_RESPONSE:
			return { ...state, isUpdatingFatcaData: false };
		case actions.CUSTOMER_F2100_ACCOUNT_NUMBER_REQUEST:
			return { ...state, isFetchingF2100AccountNumber: true };
		case actions.CUSTOMER_F2100_ACCOUNT_NUMBER_RESPONSE:
			return {
				...state,
				isFetchingF2100AccountNumber: false,
				hasFetchedF2100AccountNumber: true,
			};
		case actions.SERVER_VALIDATION_ERROR:
			if (action.data.cause === actions.EMAIL_UPDATE_REQUEST) {
				return { ...state, isUpdatingEmail: false };
			}
			return state;
		case actions.FETCH_LO_FORVALTNINGSFORM_RESPONSE:
			return { ...state, hasFetchedLoForvaltningsform: true };
		default:
			return state;
	}
};

const documentsReducer = (state: Dokumenter, action: Action): Dokumenter => {
	switch (action.type) {
		case actions.DOKUMENTER_NYE_RESPONSE:
			return { ...state, isFetching: false, hasFetched: true };
		case actions.DOKUMENTER_NYE_REQUEST:
			return { ...state, isFetching: true };
		case actions.DOKUMENTER_GAMLE_RESPONSE:
			return { ...state, isFetchingGamle: false, hasFetchedGamle: true };
		case actions.DOKUMENTER_GAMLE_REQUEST:
			return { ...state, isFetchingGamle: action.data };
		default:
			return state;
	}
};

const holdingsHistoryReducer = (state: HoldingsHistory, action: Action): HoldingsHistory => {
	switch (action.type) {
		case actions.HOLDINGS_HISTORY_RESPONSE:
			return { ...state, isFetchingHoldingsHistory: false };
		case actions.HOLDINGS_HISTORY_REQUEST:
			return { ...state, isFetchingHoldingsHistory: true };
		default:
			return state;
	}
};

const investmentProgramsReducer = (state: InvestmentPrograms, action: Action): InvestmentPrograms => {
	switch (action.type) {
		case actions.OPEN_INVESTMENT_FUNDS_REQUEST:
			return { ...state, isFetchingOpenInvestmentFunds: true };
		case actions.OPEN_INVESTMENT_FUNDS_RESPONSE:
			return {
				...state,
				isFetchingOpenInvestmentFunds: false,
				hasFetchedOpenInvestmentFunds: true,
			};
		case actions.INVESTMENT_PROFILE_UPDATE_REQUEST:
			return { ...state, isUpdatingInvestmentProfile: true };
		case actions.INVESTMENT_PROFILE_UPDATE_RESPONSE:
			return { ...state, isUpdatingInvestmentProfile: false };
		case actions.OPEN_INVESTMENT_FUNDS_UPDATE_REQUEST:
			return { ...state, isUpdatingOpenInvestmentFunds: true };
		case actions.OPEN_INVESTMENT_FUNDS_UPDATE_RESPONSE:
			return { ...state, isUpdatingOpenInvestmentFunds: false };
		default:
			return state;
	}
};

const newPolicyReducer = (state: NewPolicy, action: Action): NewPolicy => {
	switch (action.type) {
		default:
			return state;
	}
};

const policiesReducer = (state: Policies, action: Action): Policies => {
	switch (action.type) {
		case actions.CREATE_PAYMENT_DEAL_REQUEST:
			return { ...state, isCreatingPaymentDeal: true };
		case actions.CREATE_PAYMENT_DEAL_RESPONSE:
			return { ...state, isCreatingPaymentDeal: false };
		case actions.DELETE_PAYMENT_DEAL_REQUEST:
			return { ...state, isDeletingPaymentDeal: true };
		case actions.DELETE_PAYMENT_DEAL_RESPONSE:
			return { ...state, isDeletingPaymentDeal: false };
		case actions.AGGREGATED_POLICY_RESPONSE:
		case actions.POLICY_DETAILS_RESPONSE:
			return { ...state, isFetchingDetails: false };
		case actions.NJORD_POLICY_DETAILS_RESPONSE:
			return { ...state, isFetchingNjordDetails: false };
		case actions.POLICIES_RESPONSE:
			return {
				...state,
				isFetching: false,
				hasFetchedList: true,
			};
		case actions.HOLDINGS_RESPONSE:
			return { ...state, isFetchingHoldings: false };
		case actions.SALARY_HISTORY_RESPONSE:
			return { ...state, isFetchingSalaryHistory: false };
		case actions.AGGREGATED_POLICY_REQUEST:
		case actions.POLICY_DETAILS_REQUEST:
			return { ...state, isFetchingDetails: true };
		case actions.NJORD_POLICY_DETAILS_REQUEST:
			return { ...state, isFetchingNjordDetails: true };
		case actions.HOLDINGS_REQUEST:
			return { ...state, isFetchingHoldings: true };
		case actions.POLICIES_REQUEST:
			return { ...state, isFetching: true };
		case actions.SALARY_HISTORY_REQUEST:
			return { ...state, isFetchingSalaryHistory: true };
		case actions.UPDATE_UTBETALING_START_RESPONSE:
			return { ...state, isUpdatingUtbetalingStart: false };
		case actions.UPDATE_NEDTRAPPING_RESPONSE:
			return { ...state, isUpdatingNedtrapping: false };
		case actions.UPDATE_KAPITALFORVALTER_RESPONSE:
			return { ...state, isUpdatingKapitalforvalter: false };
		case actions.UPDATE_FORVALTNING_RESPONSE:
			return { ...state, isUpdatingForvaltning: false };
		case actions.UPDATE_PAYMENT_DEAL_RESPONSE:
			return { ...state, isUpdatingPaymentDeal: false };
		case actions.UPDATE_SAVING_AMOUNT_RESPONSE:
		case actions.UPDATE_SAVING_AMOUNT_FAILURE:
			return { ...state, isUpdatingSavingAmount: false };
		case actions.UPDATE_UTBETALING_START_REQUEST:
			return { ...state, isUpdatingUtbetalingStart: true };
		case actions.UPDATE_KAPITALFORVALTER_REQUEST:
			return { ...state, isUpdatingKapitalforvalter: true };
		case actions.UPDATE_NEDTRAPPING_REQUEST:
			return { ...state, isUpdatingNedtrapping: true };
		case actions.UPDATE_FORVALTNING_REQUEST:
			return { ...state, isUpdatingForvaltning: true };
		case actions.UPDATE_PAYMENT_DEAL_REQUEST:
			return { ...state, isUpdatingPaymentDeal: true };
		case actions.UPDATE_SAVING_AMOUNT_REQUEST:
			return { ...state, isUpdatingSavingAmount: true };
		case actions.STOP_SAVINGS_REQUEST:
			return { ...state, isStoppingSavings: true };
		case actions.STOP_SAVINGS_RESPONSE:
			return { ...state, isStoppingSavings: false };
		case actions.LO_PRODUKTBYTTE_REQUEST:
			return { ...state, isPerformingLoProduktbytte: true };
		case actions.LO_PRODUKTBYTTE_RESPONSE:
			return {
				...state,
				isPerformingLoProduktbytte: false,
				hasPerformedLoProduktbytte: true,
			};
		default:
			return state;
	}
};

const prognosisReducer = (state: Prognosis, action: Action): Prognosis => {
	switch (action.type) {
		case actions.POLICY_PROGNOSIS_REQUEST:
			return { ...state, isFetchingPrognosis: true };
		case actions.POLICY_PROGNOSIS_RESPONSE:
		case actions.POLICY_PROGNOSIS_FAILURE:
			return { ...state, isFetchingPrognosis: false };
		default:
			return state;
	}
};

const samtykkeReducer = (state: Samtykke, action: Action): Samtykke => {
	switch (action.type) {
		case actions.SAMTYKKE_TEXTS_REQUEST:
			return { ...state, isFetchingTexts: true };
		case actions.SAMTYKKE_TEXTS_RESPONSE:
			return {
				...state,
				hasFetchedTexts: true,
				isFetchingTexts: false,
			};
		default:
			return state;
	}
};

const slReducer = (state: SL, action: Action): SL => {
	switch (action.type) {
		case actions.CANCEL_SL_REQUEST:
			return { ...state, isCancellingSl: true };
		case actions.CANCEL_SL_RESPONSE:
			return {
				...state,
				isCancellingSl: false,
				hasCancelledSl: true,
			};
		case actions.FIND_SL_AVTALER_REQUEST:
			return { ...state, isFetchingSlAvtaler: true };
		case actions.FIND_SL_AVTALER_RESPONSE:
			return {
				...state,
				isFetchingSlAvtaler: false,
				hasFetchedSlAvtaler: true,
			};
		case actions.CREATE_SL_REQUEST:
			return { ...state, isCreatingSl: true };
		case actions.CREATE_SL_RESPONSE:
			return {
				...state,
				isCreatingSl: false,
				hasCreatedSl: true,
			};
		default:
			return state;
	}
};

const norskPensjonReducer = (state: NorskPensjon, action: Action): NorskPensjon => {
	switch (action.type) {
		case actions.NORSK_PENSJON_PKBS_REQUEST:
			return { ...state, isFetchingPkbs: true };
		case actions.NORSK_PENSJON_PKBS_RESPONSE:
			return { ...state, isFetchingPkbs: false };
		default:
			return state;
	}
};

const flyttPkbReducer = (state: FlyttPkb, action: Action): FlyttPkb => {
	switch (action.type) {
		case actions.CREATE_WORK_ORDERS_REQUEST:
			return { ...state, isCreatingWorkOrders: true };
		case actions.CREATE_WORK_ORDERS_RESPONSE:
			return { ...state, isCreatingWorkOrders: false };
		default:
			return state;
	}
};

export default function metadata(state = defaultState, action: Action): MetadataState {
	return {
		accountTransactions: accountTransactionsReducer(state.accountTransactions, action),
		bankAccounts: bankAccountsReducer(state.bankAccounts, action),
		countries: countriesReducer(state.countries, action),
		customer: customerReducer(state.customer, action),
		documents: documentsReducer(state.documents, action),
		holdingsHistory: holdingsHistoryReducer(state.holdingsHistory, action),
		investmentPrograms: investmentProgramsReducer(state.investmentPrograms, action),
		policies: policiesReducer(state.policies, action),
		newPolicy: newPolicyReducer(state.newPolicy, action),
		prognosis: prognosisReducer(state.prognosis, action),
		samtykke: samtykkeReducer(state.samtykke, action),
		sl: slReducer(state.sl, action),
		norskPensjon: norskPensjonReducer(state.norskPensjon, action),
		flyttPkb: flyttPkbReducer(state.flyttPkb, action),
	};
}
